import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, MessageSquareQuote, Share2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, getRecent } from "@/lib/api";
import { CityPill } from "@/components/dm/CityPicker";
import { Feed, FilterBar, CategoryTiles } from "@/components/dm/Feed";
import { Waitlist } from "@/components/dm/EmptyState";
import { LocationBanner } from "@/components/dm/LocationBanner";
import { WeeklyDigest } from "@/components/dm/WeeklyDigest";
import { Spotlight } from "@/components/dm/Spotlight";
import { InviteCard } from "@/pages/Invite";
import { AnkaraStrip, Photo, usePriceText } from "@/components/dm/ui";

const HERO = [
  "https://images.unsplash.com/photo-1774125364917-5980fa2fba54?auto=format&fit=crop&w=1400&q=60",
  "https://images.unsplash.com/photo-1658497730270-b5f4fef00ae1?auto=format&fit=crop&w=1400&q=60",
  "https://images.unsplash.com/photo-1765584830351-b751c8937c75?auto=format&fit=crop&w=1400&q=60",
  "https://images.pexels.com/photos/17952748/pexels-photo-17952748.jpeg?auto=compress&cs=tinysrgb&w=1400",
];

const HeroSlides = () => {
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState([true, false, false, false]);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % HERO.length), 5000);
    const warm = setTimeout(() => setLoaded(HERO.map(() => true)), 1500);
    return () => { clearInterval(id); clearTimeout(warm); };
  }, []);
  return (
    <div className="absolute inset-0" aria-hidden="true" data-testid="hero-slides">
      {HERO.map((src, i) => loaded[i] && (
        <img key={src} src={src} alt="" decoding="async" fetchpriority={i === 0 ? "high" : "low"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${i === idx ? "opacity-100" : "opacity-0"}`} />
      ))}
      <div className="absolute top-6 right-5 flex gap-1.5 z-10">
        {HERO.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-5 bg-[#F59E0B]" : "w-1.5 bg-white/60"}`} />)}
      </div>
    </div>
  );
};

const Hero = () => {
  const { t } = useApp();
  const navigate = useNavigate();
  return (
    <section className="relative bg-[#1E4D3B] overflow-hidden" data-testid="hero">
      <HeroSlides />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1E4D3B] via-[#1E4D3B]/85 to-[#1E4D3B]/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#133E2F]/80 to-transparent" />
      <div className="relative max-w-6xl mx-auto px-5 pt-28 pb-10 sm:pt-40 sm:pb-14">
        <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#FFFDF9] max-w-3xl leading-[1.05]">{t("hero_title")}</h1>
        <p data-testid="hero-free-line" className="mt-4 text-lg md:text-2xl font-extrabold text-[#F59E0B] max-w-2xl leading-snug">{t("hero_free")}</p>
        <p className="mt-3 text-base md:text-lg text-white/90 max-w-2xl">{t("hero_tagline")}</p>
        <p data-testid="hero-trust" className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-sm font-semibold text-[#FDE68A]">{t("hero_trust")}</p>
        <div className="mt-6 flex flex-col xs:flex-row sm:flex-row gap-3">
          <button data-testid="hero-sell-button" onClick={() => navigate("/create?type=listing")} className="press min-h-[52px] px-6 rounded-full bg-[#F59E0B] hover:bg-[#FBBF24] text-[#291E18] font-extrabold inline-flex items-center justify-center gap-2 shadow-lg"><Plus className="w-5 h-5" />{t("sell_something")}</button>
          <button data-testid="hero-request-button" onClick={() => navigate("/create?type=request")} className="press min-h-[52px] px-6 rounded-full border-2 border-white text-white hover:bg-white/10 font-extrabold inline-flex items-center justify-center gap-2"><MessageSquareQuote className="w-5 h-5" />{t("make_request")}</button>
        </div>
      </div>
      <AnkaraStrip className="relative" />
    </section>
  );
};

const Greeting = () => {
  const { t, user, hub, L } = useApp();
  return (
    <section className="bg-[#1E4D3B] text-white" data-testid="greeting">
      <div className="max-w-6xl mx-auto px-5 py-8">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight max-w-2xl">{t("greeting", { name: user.name.split(" ")[0], city: L(hub) })}</h1>
        <p data-testid="greeting-free-line" className="mt-2 font-bold text-[#F59E0B]">{t("hero_free")}</p>
      </div>
      <AnkaraStrip />
    </section>
  );
};

export const SearchBar = ({ initial = "", onSubmit, autoFocus }) => {
  const { t } = useApp();
  const [q, setQ] = useState(initial);
  useEffect(() => setQ(initial), [initial]);
  return (
    <form role="search" onSubmit={(e) => { e.preventDefault(); onSubmit(q.trim()); }} className="relative flex-1 min-w-[220px]">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B5E55]" aria-hidden="true" />
      <input data-testid="search-input" autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} aria-label={t("search_placeholder")}
        className="w-full min-h-[52px] rounded-full bg-[#FFFDF9] border-2 border-[#E8E2D9] focus:border-[#1E4D3B] outline-none pl-12 pr-4 text-base shadow-md" />
    </form>
  );
};

const CommunityStrip = ({ hub }) => {
  const { t, L } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats", { params: { hub: hub.id } }).then((r) => setS(r.data)).catch(() => {}); }, [hub.id]);
  if (!s) return <div className="h-6" />;
  const parts = [s.posts_week > 0 && t("strip_posts", { n: s.posts_week, city: L(hub) }), s.members > 0 && t("strip_members", { n: s.members }), s.vouches > 0 && t("strip_vouches", { n: s.vouches })].filter(Boolean);
  return (
    <p data-testid="community-strip" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-[#1E4D3B]">
      <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" aria-hidden="true" />
      {parts.map((p, i) => <span key={i}>{i > 0 && <span className="text-[#D97706] mr-2">·</span>}{p}</span>)}
    </p>
  );
};

const ShareNudge = () => {
  const { t, user } = useApp();
  const [live, setLive] = useState(false);
  useEffect(() => { api.get("/referral/campaign").then((r) => setLive(r.data.live)).catch(() => {}); }, []);
  return (
    <Link to={live && user ? "/invite" : "/share"} data-testid="home-share-nudge" className="press group flex items-center gap-2 text-xs sm:text-sm text-[#6B5E55] hover:text-[#1E4D3B]">
      <span className="w-7 h-7 rounded-full bg-[#FEF3C7] text-[#D97706] flex items-center justify-center shrink-0"><Share2 className="w-3.5 h-3.5" aria-hidden="true" /></span>
      <span>{t("share_nudge")}{live && <> <b className="text-[#92400E]">{t("share_nudge_reward")}</b></>} <span className="font-bold text-[#1E4D3B] underline underline-offset-2">{t("share_nudge_cta")}</span></span>
    </Link>
  );
};

const Recent = () => {
  const { t, cat } = useApp();
  const price = usePriceText();
  const items = getRecent();
  if (items.length < 1) return null;
  return (
    <section className="space-y-3" data-testid="recently-viewed">
      <h2 className="text-base md:text-lg font-extrabold">{t("recently_viewed")}</h2>
      <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4">
        {items.map((p) => (
          <Link key={p.id} to={`/post/${p.id}`} data-testid={`recent-${p.id}`} className="press shrink-0 w-40 rounded-2xl bg-[#FFFDF9] border border-[#E8E2D9] overflow-hidden">
            {p.type === "request" ? <div className="h-24 bg-[#FFF4EA] border-b-4 border-[#C2410C] flex items-center justify-center"><MessageSquareQuote className="w-8 h-8 text-[#C2410C]" /></div>
              : <Photo src={p.photo} alt={p.title} icon={cat(p.category_id)?.icon} className="h-24 w-full" />}
            <div className="p-2.5"><div className="text-sm font-bold line-clamp-2 leading-snug">{p.title}</div><div className="text-xs font-bold text-[#92400E] mt-1">{price(p)}</div></div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  const { user, hub, ready } = useApp();
  const navigate = useNavigate();
  const [f, setF] = useState({ category: "", type: "", status: "active" });
  const set = (p) => setF((s) => ({ ...s, ...p }));
  if (!ready || !hub) return <div className="min-h-screen" />;
  const soon = hub.launch_status !== "active";
  return (
    <div>
      {user ? <Greeting /> : <Hero />}
      <div className="max-w-6xl mx-auto px-4 -mt-0 pt-5 space-y-8">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3"><CityPill /><SearchBar onSubmit={(q) => navigate(`/explore?q=${encodeURIComponent(q)}`)} /></div>
          <CommunityStrip hub={hub} />
          <ShareNudge />
          <LocationBanner />
        </div>
        {soon ? <Waitlist hub={hub} /> : (<>
          <Spotlight hub={hub} />
          {user && <WeeklyDigest hub={hub} />}
          {user && <InviteCard />}
          <Recent />
          <section className="space-y-4"><CategoryTiles value={f.category} onChange={(c) => set({ category: c })} /><FilterBar f={f} set={set} /></section>
          <Feed hub={hub} filters={f} />
        </>)}
      </div>
    </div>
  );
}
